import React, { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  Alert,
  Box,
  Button,
  Card,
  CardBody,
  Switch,
  Text,
} from '@razorpay/blade/components';
import {
  BASE_COMPENSATION_FLOOR_SETTINGS_QUERY_KEY,
  GratuityLeUpdateRequest,
} from 'api/companyOnboarding';
import api from 'api';
import { fetchLoggedInUser } from 'reducers/loggedInUser';
import { routePaths } from 'components/Routes/data';
import { AppError } from 'utils/AppError';
import Errors from 'components/ui/Form/Errors';
import { trackSegment } from 'utils/segment';
import useFeature from 'hooks/useFeature';
import { ResetLocalOverridingStyles } from 'components/RunPayroll/ComplianceActions/styles';

const FormWrapper = styled.form(
  ({ theme }) => `
  width: 100%;
  padding-bottom: ${theme.spacing[10]}px;
`,
);

const GratuityLeaveEncashmentSetup = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { isReArchMilestone1Enabled: isRearch } = useFeature('reArchMilestone1');
  const { isBaseCompensationFloorEnabled } = useFeature('baseCompensationFloor');
  const isBaseCompFloorAvailable = isRearch && isBaseCompensationFloorEnabled;

  const [applyBaseCompFloorToGratuity, setApplyBaseCompFloorToGratuity] = useState(false);
  const [applyBaseCompFloorToLeaveEncashment, setApplyBaseCompFloorToLeaveEncashment] =
    useState(false);

  const { data: baseCompensationFloorSettings } = useQuery(
    [BASE_COMPENSATION_FLOOR_SETTINGS_QUERY_KEY],
    api.companyOnboarding.getBaseCompensationFloorSettings,
    { enabled: isBaseCompFloorAvailable },
  );

  useEffect(() => {
    if (baseCompensationFloorSettings) {
      setApplyBaseCompFloorToGratuity(baseCompensationFloorSettings.applyBaseCompFloorToGratuity);
      setApplyBaseCompFloorToLeaveEncashment(
        baseCompensationFloorSettings.applyBaseCompFloorToLeaveEncashment,
      );
    }
  }, [baseCompensationFloorSettings]);

  const updateGratuityLeMutation = useMutation(
    ({
      applyBaseCompFloorToGratuity,
      applyBaseCompFloorToLeaveEncashment,
    }: GratuityLeUpdateRequest) =>
      api.companyOnboarding.updateGratuityLeSetup({
        applyBaseCompFloorToGratuity,
        applyBaseCompFloorToLeaveEncashment,
      }),
    {
      onSuccess: () => {
        trackSegment('onboarding.setup_compliance.gratuity_leave_encashment.save_click');
        queryClient.invalidateQueries([BASE_COMPENSATION_FLOOR_SETTINGS_QUERY_KEY]);
        dispatch(fetchLoggedInUser());
        window.location.href = routePaths.phpRoutes.dashboard;
      },
      onError: (error: typeof AppError) => {},
    },
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!isBaseCompFloorAvailable) {
      return;
    }
    updateGratuityLeMutation.mutate({
      applyBaseCompFloorToGratuity,
      applyBaseCompFloorToLeaveEncashment,
    });
  };

  const isFormBeingSubmitted = updateGratuityLeMutation.status === 'loading';

  return (
    <ResetLocalOverridingStyles>
      <FormWrapper onSubmit={handleSubmit}>
        <Box
          display="flex"
          flexDirection="column"
          gap="spacing.8"
          paddingBottom="spacing.8"
          width="100%"
          maxWidth="512px"
          marginX="auto">
          <Box display="flex" flexDirection="column" gap="spacing.2">
            <Text size="large" weight="semibold" textAlign="left">
              Gratuity & Leave Encashment
            </Text>
            <Text size="medium" color="surface.text.gray.muted" textAlign="left">
              Configure the Base Compensation floor applied at Full & Final settlement.
            </Text>
          </Box>

          {isBaseCompFloorAvailable ? (
            <Card
              elevation="none"
              borderRadius="large"
              backgroundColor="surface.background.gray.intense">
              <CardBody>
                <Box display="flex" flexDirection="column" gap="spacing.6" width="100%">
                  <Box display="flex" flexDirection="column" gap="spacing.2">
                    <Text size="large" weight="semibold" textAlign="left">
                      Base Compensation floor
                    </Text>
                    <Text size="medium" color="surface.text.gray.muted" textAlign="left">
                      Enforce that these wages are at least 50% of Base Compensation. Skipped when a
                      manual amount is entered at FnF.
                    </Text>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap="spacing.4"
                    width="100%">
                    <Text size="medium" weight="regular" textAlign="left">
                      Apply Base Compensation floor to Gratuity wages
                    </Text>
                    <Switch
                      accessibilityLabel="Apply Base Compensation floor to Gratuity wages"
                      isChecked={applyBaseCompFloorToGratuity}
                      isDisabled={isFormBeingSubmitted}
                      onChange={({ isChecked }) => setApplyBaseCompFloorToGratuity(isChecked)}
                    />
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap="spacing.4"
                    width="100%">
                    <Text size="medium" weight="regular" textAlign="left">
                      Apply Base Compensation floor to Leave Encashment wages
                    </Text>
                    <Switch
                      accessibilityLabel="Apply Base Compensation floor to Leave Encashment wages"
                      isChecked={applyBaseCompFloorToLeaveEncashment}
                      isDisabled={isFormBeingSubmitted}
                      onChange={({ isChecked }) =>
                        setApplyBaseCompFloorToLeaveEncashment(isChecked)
                      }
                    />
                  </Box>
                </Box>
              </CardBody>
            </Card>
          ) : (
            <Alert
              color="information"
              description="Base Compensation floor settings are available for organisations on the new payroll engine."
              isDismissible={false}
              isFullWidth
            />
          )}

          {updateGratuityLeMutation.status === 'error' && (
            <Alert
              color="negative"
              description={<Errors errorResponse={updateGratuityLeMutation.error} />}
              isDismissible={false}
              isFullWidth
            />
          )}

          <Box
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            paddingTop="spacing.5">
            <Button
              variant="primary"
              type="submit"
              isLoading={isFormBeingSubmitted}
              isDisabled={!isBaseCompFloorAvailable}>
              Save & Next
            </Button>
          </Box>
        </Box>
      </FormWrapper>
    </ResetLocalOverridingStyles>
  );
};

export default GratuityLeaveEncashmentSetup;
